import React, { useMemo, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, CircularProgress, Alert, Button } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { Rights, Roles, User, useRolesRightsMappingQuery, ProductArea, usePermissionsLazyQuery } from '../../graphql/types';
import MainCard from '../../components/MainCard';
import ScrollX from '../../components/ScrollX';
import useConfig from '../../hooks/useConfig';
import AreaCellEditor from '../../components/ag-grid/AreaCellEditor';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import AreaChip from '../../components/ag-grid/AreaChip';
import { ReloadOutlined } from '@ant-design/icons';
import RolesAutocomplete from '../../components/RolesAutocomplete';

interface Props {
  entity?: User;
  roles?: Roles[];
  onRolesChange?: (roles: Roles[]) => void;
}

interface PermissionRow {
  permission: string;
  enabled: boolean;
  area: ProductArea | undefined;
  description: string | undefined;
}
const ALL_AREAS = Object.values(ProductArea);

export interface PermissionsTabRef {
  getPermissions: () => Rights[];
  hasCustomPermissions: () => boolean;
  resetToDefaults: () => void;
}

const nonEditableRights = new Set<Rights>([
  Rights.IsIsoManager,
  Rights.IsMerchant,
  Rights.IsBroker,
  Rights.IsInvestor,
  Rights.IsFunder,
  Rights.IsUser,
  Rights.IsSubmission,
  Rights.IsSubmissionAdmin,
  Rights.IsCollection,
  Rights.IsLegal,
  Rights.IsPreUnderwriter,
  Rights.IsUnderwriter,
  Rights.IsSuperAdmin
]);

const PermissionsTab = forwardRef<PermissionsTabRef, Props>(({ entity, roles, onRolesChange }, ref) => {
  const { mode } = useConfig();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%', minHeight: 400 }), []);
  const [showOnlyEnabled, setShowOnlyEnabled] = useState(true);
  const [customPermissions, setCustomPermissions] = useState<Map<Rights, boolean>>(new Map());
  const [permissionsData, setPermissionsData] = useState<any[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Roles[]>(roles || []);

  const { data, loading, error } = useRolesRightsMappingQuery({
    fetchPolicy: 'cache-first'
  });

  const [fetchPermissions] = usePermissionsLazyQuery({
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    const loadPermissions = async () => {
      const res = await fetchPermissions({ variables: { limit: 200, offset: 0 } });
      setPermissionsData(res.data?.permissions?.data || []);
    };
    loadPermissions();
  }, [fetchPermissions]);
    useEffect(() => {
      setSelectedRoles(roles || []);
    }, [roles]);

  const rolesMap = useMemo(() => {
    if (!data?.rolesRightsMapping) return new Map<Roles, Rights[]>();

    const map = new Map<Roles, Rights[]>();
    data.rolesRightsMapping.forEach((mapping) => {
      map.set(mapping.role as Roles, mapping.rights as Rights[]);
    });
    return map;
  }, [data]);

  const defaultRights = useMemo(() => {
    const set = new Set<Rights>();
    selectedRoles?.forEach((role) => {
      rolesMap.get(role)?.forEach((r) => set.add(r));
    });
    return set;
  }, [selectedRoles, rolesMap]);

  useEffect(() => {
    if (!entity?.rights || !defaultRights.size || isInitialized) return;

    const customMap = new Map<Rights, boolean>();
    const entityRights = new Set(entity.rights);

    Object.values(Rights).forEach((right) => {
      const hasRight = entityRights.has(right as Rights);
      const isDefault = defaultRights.has(right as Rights);

      if (hasRight !== isDefault) {
        customMap.set(right as Rights, hasRight);
      }
    });

    setCustomPermissions(customMap);
    setIsInitialized(true);
  }, [entity?.rights, defaultRights, isInitialized]);

  const allPermissions = useMemo(() => {
    const permissionsMap = new Map<string, { area: ProductArea; description: string }>();
    permissionsData.forEach((p) => permissionsMap.set(p.name, { area: p.area, description: p.description }));

    return Object.values(Rights)
      .map((right) => {
        const permData = permissionsMap.get(right);
        const enabled = customPermissions.has(right as Rights)
          ? customPermissions.get(right as Rights)!
          : defaultRights.has(right as Rights);

        return {
          permission: right,
          enabled,
          area: permData?.area || ProductArea.Other,
          description: permData?.description
        };
      })
      .sort((a, b) => a.area.localeCompare(b.area) || a.permission.localeCompare(b.permission));
  }, [permissionsData, customPermissions, defaultRights]);

  const togglePermission = useCallback(
    (permission: Rights) => {
      if (nonEditableRights.has(permission)) return;

      setCustomPermissions((prev) => {
        const newMap = new Map(prev);
        const currentValue = prev.has(permission) ? prev.get(permission)! : defaultRights.has(permission);
        newMap.set(permission, !currentValue);
        return newMap;
      });
    },
    [defaultRights]
  );

  const hasCustomPermissions = useCallback((): boolean => {
    return Array.from(customPermissions.entries()).some(([right, enabled]) => enabled !== defaultRights.has(right));
  }, [customPermissions, defaultRights]);
  const initialRoles = useMemo<Roles[]>(() => {
    if (!entity?.role) return [];
    return Array.isArray(entity.role) ? entity.role : [entity.role];
  }, [entity]);
  const resetToDefaults = useCallback(() => {
    setCustomPermissions(new Map());
    setSelectedRoles(initialRoles);
    if (onRolesChange) {
      onRolesChange(initialRoles);
    }
  }, [initialRoles, onRolesChange]);

  useImperativeHandle(
    ref,
    () => ({
      getPermissions: () => allPermissions.filter((p) => p.enabled).map((p) => p.permission as Rights),
      hasCustomPermissions,
      resetToDefaults
    }),
    [allPermissions, hasCustomPermissions]
  );

  const rowData = useMemo(() => {
    if (showOnlyEnabled) {
      return allPermissions.filter((p) => p.enabled);
    }
    return allPermissions;
  }, [allPermissions, showOnlyEnabled]);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      resizable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      tooltipComponent: DefaultAgGridTooltip
    }),
    []
  );

  const columnDefs = useMemo<ColDef<PermissionRow>[]>(
    () => [
      {
        headerName: 'Enabled',
        field: 'enabled',
        width: 100,
        headerClass: 'text-center',
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        cellRenderer: (params: any) => {
          const isLocked = nonEditableRights.has(params.data.permission as Rights);
          return (
            <Checkbox
              checked={params.value}
              size="small"
              sx={{ p: 0 }}
              disabled={isLocked}
              onChange={() => togglePermission(params.data.permission as Rights)}
            />
          );
        },
        sortable: false,
        filter: false
      },
      {
        headerName: 'Area',
        field: 'area',
        width: 140,
        editable: true,
        filter: 'agSetColumnFilter',
        filterParams: { values: ALL_AREAS },
        cellRenderer: ({ value }: ICellRendererParams) => <AreaChip area={value} />
      },
      {
        headerName: 'Permission',
        field: 'permission',
        tooltipValueGetter: (params: any) => {
          return params.data?.description;
        },
        flex: 1,
        sortable: true,
        filter: true
      }
    ],
    [togglePermission]
  );
  const availableRoles = useMemo(() => Object.values(Roles), []);

  if (loading) {
    return (
      <MainCard content={false}>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </MainCard>
    );
  }

  if (error) {
    return (
      <MainCard content={false}>
        <Box p={3}>
          <Alert severity="error">Failed to load permissions mapping: {error.message}</Alert>
        </Box>
      </MainCard>
    );
  }

  return (
    <MainCard content={false}>
        <Stack spacing={2}>
          <Box sx={{ pl: 3, pr: 3, pt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%'}}>
              <RolesAutocomplete
                roles={availableRoles}
                selectedRoles={selectedRoles}
                setSelectedRoles={setSelectedRoles}
                isInUserForm={true}
              />
              <Button
                variant="outlined"
                onClick={resetToDefaults}
                sx={{ height: 35 }}
              >
                <ReloadOutlined />
              </Button>
                <FormControlLabel
                sx={{ pl: 13 }} 
                  control={<Checkbox checked={showOnlyEnabled} onChange={(e) => setShowOnlyEnabled(e.target.checked)} size="small" />}
                  label="Show only enabled permissions"
                />
            </Stack>
          </Box>
          
          <Box display="flex" flexDirection="column" sx={{ pl: 3, pr: 3, pb: 2, maxHeight: 300,  overflowY: 'auto' }}>
            <div className={mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} style={gridStyle}>
              <AgGridReact<PermissionRow>
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                domLayout={'autoHeight'}
                rowHeight={40}
                headerHeight={40}
                suppressCellFocus={true}
                pagination={false}
                tooltipShowDelay={0}
                tooltipHideDelay={2000}
                cellSelection={true}
              />
            </div>
          </Box>
        </Stack>
    </MainCard>
  );
});

export default PermissionsTab;